import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Button,
  Card,
  Checkbox,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Space,
  Spin,
  Table,
  TimePicker,
  Tag,
} from "antd";
import moment from "moment";
import "moment-timezone";
import { useDispatch } from "react-redux";
import PageBreadcrumb from "../../component/PageBreadcrumb";
import {
  AssignAiSessionsToBatch,
  ListAiBatches,
  ListAiSessionsForAssign,
  ManualCreateAiBatch,
  UpdateAiBatch,
} from "../../redux/actions/AiSessionsAction";

const PAGE_SIZE = 10;
const dateFormat = "YYYY-MM-DD";
const IST_TZ = "Asia/Kolkata";

const getId = (obj, fallback) => obj?._id ?? obj?.id ?? fallback;

const getBatchLabel = (b) =>
  b?.name ??
  b?.batchName ??
  b?.title ??
  b?.batchTitle ??
  b?.code ??
  b?.slug ??
  getId(b, "");

const toUtcIso = (dateMoment, timeMoment) => {
  if (!dateMoment || !timeMoment) return null;
  const combined = moment(dateMoment)
    .hour(moment(timeMoment).hour())
    .minute(moment(timeMoment).minute())
    .second(0)
    .millisecond(0);
  return combined.utc().toISOString();
};

const fromUtcIsoToMoments = (iso) => {
  if (!iso) return { releaseDate: null, releaseTime: null };
  const m = moment(iso);
  if (!m.isValid()) return { releaseDate: null, releaseTime: null };
  return { releaseDate: m.clone(), releaseTime: m.clone() };
};

const findModalSessionById = (sessionId, sessionsList = []) =>
  sessionsList.find(
    (session, idx) => String(getId(session, String(idx))) === String(sessionId)
  );

const getSessionDefaultReleaseMoments = (session) => {
  const dateMoment = session?.date ? moment(session.date) : null;
  const timeStr = session?.time ?? session?.sessionTime ?? session?.startTime;

  const releaseDate = dateMoment?.isValid()
    ? dateMoment.clone().startOf("day")
    : moment().startOf("day");

  let releaseTime = moment().startOf("minute");
  if (timeStr && timeStr !== "-") {
    const parsed = moment(timeStr, ["HH:mm", "HH:mm:ss", "h:mm A"], true);
    if (parsed.isValid()) {
      releaseTime = parsed.clone();
    } else if (dateMoment?.isValid()) {
      const combined = moment(
        `${dateMoment.format(dateFormat)} ${timeStr}`,
        `${dateFormat} HH:mm`,
        true
      );
      if (combined.isValid()) {
        releaseTime = combined.clone();
      }
    }
  }

  return { releaseDate, releaseTime };
};

const AiSessions = () => {
  const dispatch = useDispatch();
  const [batchFilterForm] = Form.useForm();

  const [batchesLoading, setBatchesLoading] = useState(false);
  const [batches, setBatches] = useState([]);
  const [batchPage, setBatchPage] = useState(1);
  const [batchTotal, setBatchTotal] = useState(0);

  const [sessionNameMap, setSessionNameMap] = useState({});

  const [assigning, setAssigning] = useState(false);
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const [modalBatch, setModalBatch] = useState(null);
  const [modalSelectedSessionIds, setModalSelectedSessionIds] = useState([]);
  const [modalSessionConfig, setModalSessionConfig] = useState({});
  const [modalSessionsList, setModalSessionsList] = useState([]);
  const [modalSessionsLoading, setModalSessionsLoading] = useState(false);
  const [modalSessionPage, setModalSessionPage] = useState(1);
  const [assignNotifyChannels, setAssignNotifyChannels] = useState([
    "whatsapp",
    "email",
  ]);

  const [createBatchModalOpen, setCreateBatchModalOpen] = useState(false);
  const [createBatchForm] = Form.useForm();
  const [creatingBatch, setCreatingBatch] = useState(false);

  const [editBatchModalOpen, setEditBatchModalOpen] = useState(false);
  const [editBatchForm] = Form.useForm();
  const [editingBatch, setEditingBatch] = useState(null);
  const [updatingBatch, setUpdatingBatch] = useState(false);

  const MODAL_SESSIONS_LIMIT = 50;

  const fetchBatches = useCallback(
    (page = 1) => {
      const values = batchFilterForm.getFieldsValue();
      const payload = {
        page,
        limit: PAGE_SIZE,
        status: values?.status ?? "active",
        ...(values?.type ? { type: values.type } : {}),
        ...(values?.search ? { search: values.search } : {}),
      };

      setBatchesLoading(true);
      dispatch(
        ListAiBatches(
          payload,
          ({ list, pagination }) => {
            const safe = Array.isArray(list) ? list : [];
            setBatches(safe);
            setBatchTotal(
              pagination?.totalItems ?? pagination?.total ?? safe.length
            );
            setBatchPage(page);
            setBatchesLoading(false);
          },
          (errMsg) => {
            setBatchesLoading(false);
            Modal.error({ title: "Failed to fetch batches", content: errMsg });
          }
        )
      );
    },
    [dispatch, batchFilterForm]
  );

  const fetchModalSessions = useCallback(() => {
    setModalSessionsLoading(true);
    dispatch(
      ListAiSessionsForAssign(
        { page: 1, limit: MODAL_SESSIONS_LIMIT },
        ({ list }) => {
          const safe = Array.isArray(list) ? list : [];
          // Ensure already-assigned sessions always exist in the table dataSource.
          // Otherwise, toggling selection on visible rows can drop "unknown" keys.
          const assigned = Array.isArray(modalBatch?.sessions)
            ? modalBatch.sessions
            : [];
          const assignedIds = assigned
            .map((s) => String(s?.sessionId ?? "").trim())
            .filter(Boolean);
          const safeIdSet = new Set(
            safe.map((s, idx) => String(getId(s, String(idx))))
          );
          const missingAssignedRows = assignedIds
            .filter((sid) => !safeIdSet.has(String(sid)))
            .map((sid) => ({
              _id: sid,
              title:
                sessionNameMap?.[String(sid)] ??
                assigned.find((x) => String(x?.sessionId) === String(sid))
                  ?.title ??
                "Session",
            }));
          setModalSessionsList([...missingAssignedRows, ...safe]);
          setModalSessionsLoading(false);
        },
        (errMsg) => {
          setModalSessionsLoading(false);
          Modal.error({ title: "Failed to fetch sessions", content: errMsg });
        }
      )
    );
  }, [dispatch, modalBatch, sessionNameMap]);

  const fetchSessionCatalog = useCallback(() => {
    dispatch(
      ListAiSessionsForAssign(
        { page: 1, limit: 50 },
        ({ list }) => {
          const safe = Array.isArray(list) ? list : [];
          const nextMap = {};
          safe.forEach((item, idx) => {
            const id = getId(item, String(idx));
            nextMap[id] = item?.title ?? item?.name ?? "Session";
          });
          setSessionNameMap(nextMap);
        },
        () => {}
      )
    );
  }, [dispatch]);

  useEffect(() => {
    batchFilterForm.setFieldsValue({ status: "active" });
    fetchBatches(1);
    fetchSessionCatalog();
  }, [batchFilterForm, fetchBatches, fetchSessionCatalog]);

  const alreadyAssignedSessionIds = useMemo(() => {
    const raw = modalBatch?.sessions;
    if (!Array.isArray(raw)) return new Set();
    return new Set(
      raw
        .map((x) => String(x?.sessionId ?? "").trim())
        .filter(Boolean)
    );
  }, [modalBatch]);

  // Show all sessions in modal (including already assigned). Already assigned
  // are pre-selected and can be unchecked to remove from the batch.
  const modalAvailableSessions = useMemo(() => {
    return modalSessionsList ?? [];
  }, [modalSessionsList]);

  const modalTableData = useMemo(() => {
    const start = (modalSessionPage - 1) * PAGE_SIZE;
    const pageSlice = modalAvailableSessions.slice(start, start + PAGE_SIZE);
    return pageSlice.map((s, i) => {
      const id = getId(s, String(start + i));
      const formattedDate = s?.date
        ? moment(s.date).isValid()
          ? moment(s.date).format(dateFormat)
          : String(s.date)
        : "-";
      return {
        key: id,
        _id: id,
        slNo: start + i + 1,
        title: s?.title ?? "-",
        date: formattedDate,
        time: s?.time ?? s?.sessionTime ?? s?.startTime ?? "-",
        assigned: alreadyAssignedSessionIds.has(String(id)),
      };
    });
  }, [modalAvailableSessions, modalSessionPage, alreadyAssignedSessionIds]);

  const batchTableData = useMemo(() => {
    const offset = (batchPage - 1) * PAGE_SIZE;
    return (batches ?? []).map((b, i) => {
      const id = getId(b, String(i));
      const sessionsCount = Array.isArray(b?.sessions) ? b.sessions.length : 0;
      const startMoment = b?.enrollmentStartDate
        ? moment(b.enrollmentStartDate)
        : null;
      const endMoment = b?.enrollmentEndDate ? moment(b.enrollmentEndDate) : null;

      const enrollmentStartDate = startMoment?.isValid()
        ? startMoment.tz(IST_TZ).format(dateFormat)
        : "-";
      const enrollmentStartTime = startMoment?.isValid()
        ? startMoment.tz(IST_TZ).format("HH:mm")
        : "-";

      const enrollmentEndDate = endMoment?.isValid()
        ? endMoment.tz(IST_TZ).format(dateFormat)
        : "-";
      const enrollmentEndTime = endMoment?.isValid()
        ? endMoment.tz(IST_TZ).format("HH:mm")
        : "-";
      return {
        key: id,
        _id: id,
        slNo: offset + i + 1,
        batchName: getBatchLabel(b) || "-",
        type: b?.type ?? "-",
        status: b?.status ?? "-",
        enrollmentStartDate,
        enrollmentStartTime,
        enrollmentEndDate,
        enrollmentEndTime,
        sessionsCount,
        raw: b,
      };
    });
  }, [batches, batchPage]);

  const updateModalConfig = (sessionId, patch) => {
    setModalSessionConfig((prev) => ({
      ...prev,
      [sessionId]: {
        order: prev?.[sessionId]?.order ?? null,
        releaseDate: prev?.[sessionId]?.releaseDate ?? null,
        releaseTime: prev?.[sessionId]?.releaseTime ?? null,
        ...patch,
      },
    }));
  };

  const ensureModalConfigDefaultsForSelection = useCallback(
    (ids, sessionsList = modalSessionsList) => {
      const existing = Array.isArray(modalBatch?.sessions)
        ? modalBatch.sessions
        : [];
      const baseExistingOrder = Math.max(
        existing.length,
        existing.reduce((max, s) => Math.max(max, Number(s?.order) || 0), 0)
      );

      setModalSessionConfig((prev) => {
        const next = { ...prev };
        const newIds = (ids ?? []).filter((id) => !next[id]);
        let nextOrder =
          Math.max(
            baseExistingOrder,
            ...Object.values(next).map((cfg) => Number(cfg?.order) || 0)
          ) + 1;

        newIds.forEach((id, idx) => {
          const session = findModalSessionById(id, sessionsList);
          const { releaseDate, releaseTime } = session
            ? getSessionDefaultReleaseMoments(session)
            : { releaseDate: moment().startOf("day"), releaseTime: moment() };

          next[id] = {
            order: nextOrder + idx,
            releaseDate,
            releaseTime,
          };
        });
        return next;
      });
    },
    [modalBatch, modalSessionsList]
  );

  useEffect(() => {
    if (!isAssignModalOpen || !modalSessionsList.length || !modalSelectedSessionIds.length) {
      return;
    }

    setModalSessionConfig((prev) => {
      let changed = false;
      const next = { ...prev };

      modalSelectedSessionIds.forEach((id) => {
        const current = next[id];
        if (!current?.releaseDate || !current?.releaseTime) {
          const session = findModalSessionById(id, modalSessionsList);
          if (!session) return;
          const defaults = getSessionDefaultReleaseMoments(session);
          next[id] = {
            order: current?.order ?? null,
            releaseDate: current?.releaseDate ?? defaults.releaseDate,
            releaseTime: current?.releaseTime ?? defaults.releaseTime,
          };
          changed = true;
        }
      });

      return changed ? next : prev;
    });
  }, [isAssignModalOpen, modalSessionsList, modalSelectedSessionIds]);

  const openAssignModalForBatch = useCallback((batchRecord) => {
    setModalBatch(batchRecord);
    const existing = Array.isArray(batchRecord?.sessions)
      ? batchRecord.sessions
      : [];
    const existingIds = existing
      .map((s) => String(s?.sessionId ?? "").trim())
      .filter(Boolean);
    setModalSelectedSessionIds(existingIds);

    const seededConfig = {};
    existing.forEach((s) => {
      const sid = String(s?.sessionId ?? "").trim();
      if (!sid) return;
      const { releaseDate, releaseTime } = fromUtcIsoToMoments(s?.releaseAt);
      seededConfig[sid] = {
        order: s?.order ?? null,
        releaseDate,
        releaseTime,
      };
    });
    setModalSessionConfig(seededConfig);
    setModalSessionPage(1);
    setModalSessionsList([]);
    setAssignNotifyChannels(["whatsapp", "email"]);
    setIsAssignModalOpen(true);
    fetchModalSessions();
  }, [fetchModalSessions]);

  const toMomentOrNull = (value) => {
    if (!value) return null;
    const m = moment(value);
    return m.isValid() ? m : null;
  };

  const openEditModalForBatch = useCallback(
    (batchRecord) => {
      setEditingBatch(batchRecord);
      editBatchForm.setFieldsValue({
        batchName: getBatchLabel(batchRecord) || "",
        startDate: toMomentOrNull(batchRecord?.startDate),
        endDate: toMomentOrNull(batchRecord?.endDate),
        enrollmentStartDate: toMomentOrNull(batchRecord?.enrollmentStartDate),
        enrollmentEndDate: toMomentOrNull(batchRecord?.enrollmentEndDate),
        status: batchRecord?.status ?? "active",
        type: batchRecord?.type ?? "weekday",
        capacity:
          batchRecord?.capacity != null ? Number(batchRecord.capacity) : undefined,
      });
      setEditBatchModalOpen(true);
    },
    [editBatchForm]
  );

  const modalColumns = useMemo(() => {
    return [
      { title: "Sl No", width: 70, dataIndex: "slNo", key: "slNo" },
      { title: "Title", width: 220, dataIndex: "title", key: "title" },
      { title: "Date", width: 120, dataIndex: "date", key: "date" },
      { title: "Time", width: 110, dataIndex: "time", key: "time" },
      {
        title: "Assigned",
        width: 110,
        dataIndex: "assigned",
        key: "assigned",
        render: (v) => (v ? <Tag color="green">Yes</Tag> : <Tag>No</Tag>),
      },
      {
        title: "Order",
        width: 120,
        key: "order",
        fixed: "right",
        render: (_, record) => {
          const id = record?._id;
          const disabled = !modalSelectedSessionIds.includes(id);
          return (
            <InputNumber
              min={1}
              style={{ width: "100%" }}
              disabled={disabled}
              value={modalSessionConfig?.[id]?.order ?? null}
              onChange={(val) => updateModalConfig(id, { order: val ?? null })}
              placeholder="Order"
            />
          );
        },
      },
      {
        title: "Release Date",
        width: 160,
        key: "releaseDate",
        fixed: "right",
        render: (_, record) => {
          const id = record?._id;
          const disabled = !modalSelectedSessionIds.includes(id);
          return (
            <DatePicker
              format={dateFormat}
              style={{ width: "100%" }}
              disabled={disabled}
              value={modalSessionConfig?.[id]?.releaseDate ?? null}
              onChange={(val) =>
                updateModalConfig(id, { releaseDate: val ?? null })
              }
            />
          );
        },
      },
      {
        title: "Release Time",
        width: 150,
        key: "releaseTime",
        fixed: "right",
        render: (_, record) => {
          const id = record?._id;
          const disabled = !modalSelectedSessionIds.includes(id);
          return (
            <TimePicker
              format="HH:mm"
              minuteStep={5}
              style={{ width: "100%" }}
              disabled={disabled}
              value={modalSessionConfig?.[id]?.releaseTime ?? null}
              onChange={(val) =>
                updateModalConfig(id, { releaseTime: val ?? null })
              }
            />
          );
        },
      },
    ];
  }, [modalSelectedSessionIds, modalSessionConfig]);

  const batchColumns = useMemo(() => {
    return [
      { title: "Sl No", width: 70, dataIndex: "slNo", key: "slNo" },
      { title: "Batch", dataIndex: "batchName", key: "batchName" },
      { title: "Type", width: 110, dataIndex: "type", key: "type" },
      {
        title: "Enroll Start Date",
        width: 140,
        dataIndex: "enrollmentStartDate",
        key: "enrollmentStartDate",
      },
      {
        title: "Enroll Start Time",
        width: 130,
        dataIndex: "enrollmentStartTime",
        key: "enrollmentStartTime",
      },
      {
        title: "Enroll End Date",
        width: 140,
        dataIndex: "enrollmentEndDate",
        key: "enrollmentEndDate",
      },
      {
        title: "Enroll End Time",
        width: 120,
        dataIndex: "enrollmentEndTime",
        key: "enrollmentEndTime",
      },
      {
        title: "Status",
        width: 120,
        dataIndex: "status",
        key: "status",
        render: (value) => (
          <Tag color={String(value).toLowerCase() === "active" ? "green" : "default"}>
            {value || "-"}
          </Tag>
        ),
      },
      {
        title: "Active Sessions",
        width: 140,
        dataIndex: "sessionsCount",
        key: "sessionsCount",
      },
      {
        title: "Action",
        width: 260,
        key: "action",
        render: (_, record) => (
          <Space>
            <Button type="primary" onClick={() => openAssignModalForBatch(record.raw)}>
              Add Sessions
            </Button>
            <Button onClick={() => openEditModalForBatch(record.raw)}>Edit</Button>
          </Space>
        ),
      },
    ];
  }, [openAssignModalForBatch, openEditModalForBatch]);

  const onAssignFromModal = () => {
    const batchId = getId(modalBatch, null);
    if (!batchId) {
      Modal.warning({ title: "Invalid batch selected" });
      return;
    }
    if (!modalSelectedSessionIds.length) {
      Modal.warning({ title: "Please select at least one session" });
      return;
    }

    const sessionsPayload = modalSelectedSessionIds.map((sid) => {
      const cfg = modalSessionConfig?.[sid] ?? {};
      const title =
        sessionNameMap?.[String(sid)] ??
        modalSessionsList.find((x, idx) => String(getId(x, String(idx))) === String(sid))
          ?.title ??
        "Session";
      return {
        sessionId: sid,
        title,
        order: cfg.order ?? null,
        releaseAt: toUtcIso(cfg.releaseDate, cfg.releaseTime),
      };
    });

    const invalid = sessionsPayload.find(
      (s) => !s.sessionId || !s.order || !s.releaseAt
    );
    if (invalid) {
      Modal.error({
        title: "Missing order/release time",
        content:
          "For all selected sessions, please fill both Order and Release Date/Time.",
      });
      return;
    }

    const orderSet = new Set();
    const duplicateOrder = sessionsPayload.find((s) => {
      const key = String(s.order);
      if (orderSet.has(key)) return true;
      orderSet.add(key);
      return false;
    });
    if (duplicateOrder) {
      Modal.error({
        title: "Duplicate order number",
        content:
          "Order must be unique across existing and newly selected sessions in this batch.",
      });
      return;
    }

    setAssigning(true);
    dispatch(
      AssignAiSessionsToBatch(
        {
          batchId,
          sessions: sessionsPayload,
          notificationChannels: assignNotifyChannels,
        },
        () => {
          setAssigning(false);
          setIsAssignModalOpen(false);
          setModalBatch(null);
          setModalSelectedSessionIds([]);
          setModalSessionConfig({});
          setAssignNotifyChannels(["whatsapp", "email"]);
          fetchBatches(1);
          fetchSessionCatalog();
          Modal.success({ title: "Sessions assigned successfully" });
        },
        (errMsg) => {
          setAssigning(false);
          Modal.error({ title: "Assign failed", content: errMsg });
        }
      )
    );
  };

  const onManualCreateBatch = async () => {
    const values = await createBatchForm.validateFields();
    const targetDate = moment(values.targetDate).format(dateFormat);
    const type = values?.type;
    setCreatingBatch(true);
    try {
      await new Promise((resolve, reject) => {
        dispatch(
          ManualCreateAiBatch(
            { targetDate, type },
            () => resolve(),
            (errMsg) => reject(new Error(errMsg))
          )
        );
      });
      setCreateBatchModalOpen(false);
      createBatchForm.resetFields();
      fetchBatches(batchPage);
      fetchSessionCatalog();
      Modal.success({ title: "Batch created successfully" });
    } catch (e) {
      Modal.error({
        title: "Create batch failed",
        content: e?.message || "Something went wrong",
      });
    } finally {
      setCreatingBatch(false);
    }
  };

  const onUpdateBatch = async () => {
    const batchId = getId(editingBatch, null);
    if (!batchId) {
      Modal.warning({ title: "Invalid batch selected" });
      return;
    }

    const values = await editBatchForm.validateFields();
    const payload = {
      batchName: values.batchName,
      startDate: values.startDate?.toISOString?.() ?? undefined,
      endDate: values.endDate?.toISOString?.() ?? undefined,
      enrollmentStartDate:
        values.enrollmentStartDate?.toISOString?.() ?? undefined,
      enrollmentEndDate: values.enrollmentEndDate?.toISOString?.() ?? undefined,
      status: values.status,
      type: values.type,
      ...(values.capacity != null ? { capacity: Number(values.capacity) } : {}),
    };

    setUpdatingBatch(true);
    try {
      await new Promise((resolve, reject) => {
        dispatch(
          UpdateAiBatch(
            batchId,
            payload,
            () => resolve(),
            (errMsg) => reject(new Error(errMsg))
          )
        );
      });
      setEditBatchModalOpen(false);
      setEditingBatch(null);
      editBatchForm.resetFields();
      fetchBatches(batchPage);
      Modal.success({ title: "Batch updated successfully" });
    } catch (e) {
      Modal.error({
        title: "Update batch failed",
        content: e?.message || "Something went wrong",
      });
    } finally {
      setUpdatingBatch(false);
    }
  };

  return (
    <div>
      <PageBreadcrumb route={["dashboard", "AI Sessions"]} />

      <Card title="Batch selection" style={{ marginBottom: "1rem" }}>
        <Form
          form={batchFilterForm}
          layout="vertical"
          initialValues={{ status: "active" }}
          onFinish={() => fetchBatches(1)}
        >
          <Space
            size={16}
            style={{ display: "flex", flexWrap: "wrap" }}
            align="start"
          >
            <Form.Item
              label="Status"
              name="status"
              style={{ flex: "1 1 180px" }}
            >
              <Select
                options={[
                  { label: "Active", value: "active" },
                  { label: "Inactive", value: "inactive" },
                ]}
              />
            </Form.Item>

            <Form.Item label="Type" name="type" style={{ flex: "1 1 180px" }}>
              <Select
                allowClear
                placeholder="Optional"
                options={[
                  { label: "Daily", value: "daily" },
                  { label: "Weekly", value: "weekly" },
                  { label: "Monthly", value: "monthly" },
                ]}
              />
            </Form.Item>

            <Form.Item
              label="Search"
              name="search"
              style={{ flex: "2 1 320px" }}
            >
              <Input placeholder="e.g. batch_2026" allowClear />
            </Form.Item>

            <Form.Item label=" " style={{ flex: "0 0 auto" }}>
              <Space>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={batchesLoading}
                >
                  Fetch batches
                </Button>
              </Space>
            </Form.Item>
          </Space>
        </Form>

        <div style={{ textAlign: "right", marginTop: 8 }}>
          <Space>
            <Button onClick={() => fetchBatches(batchPage)} loading={batchesLoading}>
              Refresh
            </Button>
            <Button
              type="primary"
              onClick={() => {
                createBatchForm.setFieldsValue({
                  targetDate: moment(),
                  type: "weekday",
                });
                setCreateBatchModalOpen(true);
              }}
            >
              Create batch
            </Button>
          </Space>
        </div>

        <Modal
          title="Create batch"
          visible={createBatchModalOpen}
          onCancel={() => {
            setCreateBatchModalOpen(false);
            createBatchForm.resetFields();
          }}
          onOk={onManualCreateBatch}
          confirmLoading={creatingBatch}
          okText="Create"
        >
          <Form form={createBatchForm} layout="vertical">
            <Form.Item
              label="Batch type"
              name="type"
              rules={[{ required: true, message: "Please select batch type" }]}
            >
              <Select
                options={[
                  { label: "Weekday", value: "weekday" },
                  { label: "Weekly", value: "weekly" },
                ]}
              />
            </Form.Item>
            <Form.Item
              label="Target date"
              name="targetDate"
              rules={[{ required: true, message: "Please select target date" }]}
            >
              <DatePicker style={{ width: "100%" }} format={dateFormat} />
            </Form.Item>
          </Form>
        </Modal>

        <Modal
          title="Edit batch"
          visible={editBatchModalOpen}
          onCancel={() => {
            setEditBatchModalOpen(false);
            setEditingBatch(null);
            editBatchForm.resetFields();
          }}
          onOk={onUpdateBatch}
          confirmLoading={updatingBatch}
          okText="Save"
          width={560}
        >
          <Form form={editBatchForm} layout="vertical">
            <Form.Item
              label="Batch name"
              name="batchName"
              rules={[{ required: true, message: "Please enter batch name" }]}
            >
              <Input placeholder="e.g. updated_batch_name_01" />
            </Form.Item>
            <Form.Item label="Start date" name="startDate">
              <DatePicker
                showTime
                style={{ width: "100%" }}
                format={`${dateFormat} HH:mm`}
              />
            </Form.Item>
            <Form.Item label="End date" name="endDate">
              <DatePicker
                showTime
                style={{ width: "100%" }}
                format={`${dateFormat} HH:mm`}
              />
            </Form.Item>
            <Form.Item label="Enrollment start date" name="enrollmentStartDate">
              <DatePicker
                showTime
                style={{ width: "100%" }}
                format={`${dateFormat} HH:mm`}
              />
            </Form.Item>
            <Form.Item label="Enrollment end date" name="enrollmentEndDate">
              <DatePicker
                showTime
                style={{ width: "100%" }}
                format={`${dateFormat} HH:mm`}
              />
            </Form.Item>
            <Form.Item
              label="Status"
              name="status"
              rules={[{ required: true, message: "Please select status" }]}
            >
              <Select
                options={[
                  { label: "Active", value: "active" },
                  { label: "Inactive", value: "inactive" },
                ]}
              />
            </Form.Item>
            <Form.Item
              label="Batch type"
              name="type"
              rules={[{ required: true, message: "Please select batch type" }]}
            >
              <Select
                options={[
                  { label: "Weekday", value: "weekday" },
                  { label: "Weekly", value: "weekly" },
                ]}
              />
            </Form.Item>
            <Form.Item label="Capacity" name="capacity">
              <InputNumber min={1} style={{ width: "100%" }} />
            </Form.Item>
          </Form>
        </Modal>

        {batchesLoading ? (
          <div className="spin-center" style={{ marginTop: 12 }}>
            <Spin />
          </div>
        ) : (
          <Table
            style={{ marginTop: 12 }}
            dataSource={batchTableData}
            columns={batchColumns}
            expandable={{
              rowExpandable: (record) => (record?.sessionsCount ?? 0) > 0,
              expandedRowRender: (record) => {
                const sessionsInBatch = Array.isArray(record?.raw?.sessions)
                  ? [...record.raw.sessions]
                  : [];
                if (!sessionsInBatch.length) return <span>No sessions</span>;
                sessionsInBatch.sort((a, b) => (a?.order ?? 0) - (b?.order ?? 0));
                return (
                  <Space size={[6, 6]} wrap>
                    {sessionsInBatch.map((s) => (
                      <Tag key={s?._id ?? `${s?.sessionId}-${s?.order}`}>
                        {sessionNameMap?.[String(s?.sessionId)] ?? "Session"}
                      </Tag>
                    ))}
                  </Space>
                );
              },
            }}
            pagination={{
              current: batchPage,
              pageSize: PAGE_SIZE,
              total: batchTotal,
              showSizeChanger: false,
              onChange: (page) => fetchBatches(page),
            }}
          />
        )}
      </Card>
      <Modal
        title={`Add sessions to ${getBatchLabel(modalBatch) || "batch"}`}
        visible={isAssignModalOpen}
        width={1200}
        onCancel={() => {
          setIsAssignModalOpen(false);
          setModalBatch(null);
          setModalSelectedSessionIds([]);
          setModalSessionConfig({});
          setAssignNotifyChannels(["whatsapp", "email"]);
        }}
        footer={
          <Space>
            <Button
              onClick={() => {
                setIsAssignModalOpen(false);
                setModalBatch(null);
                setModalSelectedSessionIds([]);
                setModalSessionConfig({});
                setModalSessionsList([]);
                setModalSessionPage(1);
                setAssignNotifyChannels(["whatsapp", "email"]);
              }}
            >
              Cancel
            </Button>
            <Button type="primary" onClick={onAssignFromModal} loading={assigning}>
              Assign selected
            </Button>
          </Space>
        }
      >
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            gap: 12,
            marginBottom: 12,
            flexWrap: "wrap",
          }}
        >
          <span style={{ color: "rgba(0,0,0,0.65)" }}>Notify learners via</span>
          <Checkbox.Group
            value={assignNotifyChannels}
            onChange={(vals) =>
              setAssignNotifyChannels((vals ?? []).map(String))
            }
            options={[
              { label: "WhatsApp", value: "whatsapp" },
              { label: "Email", value: "email" },
            ]}
          />
        </div>
        <Table
          dataSource={modalTableData}
          rowKey="_id"
          columns={modalColumns}
          loading={modalSessionsLoading}
          rowSelection={{
            selectedRowKeys: modalSelectedSessionIds,
            preserveSelectedRowKeys: true,
            onChange: (keys) => {
              const ids = keys.map(String);
              setModalSelectedSessionIds(ids);
              ensureModalConfigDefaultsForSelection(ids);
            },
          }}
          pagination={{
            current: modalSessionPage,
            pageSize: PAGE_SIZE,
            total: modalAvailableSessions.length,
            showSizeChanger: false,
            showTotal: (total) => (total > 0 ? `${total} sessions` : "0 sessions"),
            onChange: (page) => setModalSessionPage(page),
          }}
          scroll={{ x: 1200 }}
        />
      </Modal>
    </div>
  );
};

export default AiSessions;

